/**
 * @file event-form.service.js
 * @module EventFormService
 * @description Maps events between the API and the event form. The form keeps
 * dates as "YYYY-MM-DD" calendar days and the customer as its id.
 */

import moment from "moment-timezone";
import {
  EVENT_STATUS,
  EVENT_CODE_PATTERN
} from "../constants/events.constants";

const EVENT_FORM_FIELDS = [
  "name",
  "code",
  "description",
  "startDate",
  "endDate",
  "timezone",
  "status",
  "customer"
];

/**
 * Form of a new event.
 * @returns {Object}
 */
export const buildEmptyEventForm = () => ({
  name: "",
  code: "",
  description: "",
  startDate: "",
  endDate: "",
  timezone: "",
  status: EVENT_STATUS.ACTIVE,
  customer: null
});

/**
 * Converts a stored event date to the form value.
 * @param {string|Date} date
 * @returns {string} "YYYY-MM-DD", or "" without date.
 */
const toFormDate = date => (date ? moment.utc(date).format("YYYY-MM-DD") : "");

/**
 * Form of an event returned by the API.
 * @param {Object} event - Event with populated customer.
 * @returns {Object}
 */
export const buildEventFormFromEvent = (event = {}) => ({
  name: event.name || "",
  code: event.code || "",
  description: event.description || "",
  startDate: toFormDate(event.startDate),
  endDate: toFormDate(event.endDate),
  timezone: event.timezone || "",
  status: event.status || EVENT_STATUS.ACTIVE,
  customer: event.customer ? event.customer._id : null
});

/**
 * Payload to create an event from the form. The code is sent uppercase, as
 * the backend stores it.
 * @param {Object} form
 * @returns {Object}
 */
export const buildCreateEventPayload = form => ({
  name: form.name.trim(),
  code: form.code.trim().toUpperCase(),
  description: form.description,
  startDate: form.startDate,
  endDate: form.endDate,
  timezone: form.timezone,
  status: form.status,
  customer: form.customer || null
});

/**
 * Payload to update an event: only the fields that changed from the loaded
 * event, since the backend updates the fields it receives.
 * @param {Object} form
 * @param {Object} originalForm - Form as it was loaded.
 * @returns {Object}
 */
export const buildUpdateEventPayload = (form, originalForm) => {
  const payload = buildCreateEventPayload(form);
  const original = buildCreateEventPayload(originalForm);
  return EVENT_FORM_FIELDS.filter(
    field => payload[field] !== original[field]
  ).reduce((changes, field) => ({ ...changes, [field]: payload[field] }), {});
};

/**
 * Whether the code uses only letters, numbers, "_" and "-".
 * @param {string} code
 * @returns {boolean}
 */
export const isValidEventCode = code =>
  !code || EVENT_CODE_PATTERN.test(code.trim());

/**
 * Whether the end date is on or after the start date.
 * @param {string} endDate - "YYYY-MM-DD".
 * @param {string} startDate - "YYYY-MM-DD".
 * @returns {boolean}
 */
export const isEndDateValid = (endDate, startDate) =>
  !endDate || !startDate || endDate >= startDate;
